import React, { useEffect } from 'react';
import { RefreshCw, CheckCircle2, AlertTriangle, CircleDashed, Clock } from 'lucide-react';
import { Badge } from './Badge';
import { Button } from './Button';
import {
  useEngineSyncStatus, useSyncServiceToEngine, isBookingEngineSyncEnabled,
  extractEngineSyncErrorMessage,
} from '../../hooks/useServices';

export interface BookingEngineSyncStatusProps {
  serviceId: string | null | undefined;
  className?: string;
}

/**
 * SAM-194 step 1.5 — small status block for the service editor showing whether this service has
 * been mirrored into the booking engine (sam-booking-service), plus a manual "Sync now" button.
 *
 * New Tailwind component (BaseUI/Styletron freeze, SAM-175) mounted inside the grandfathered
 * ServiceDetailDrawer.tsx with the minimal touch of an import + one JSX line — see that file.
 *
 * Renders nothing when VITE_BOOKING_ENGINE_SYNC is off (default) or there is no saved service
 * yet (a service must exist in SAM before it can be mirrored into the engine).
 *
 * SAM-568 — a sync can now run past API Gateway's 29s limit and keep going in the background
 * (`useSyncServiceToEngine` polls GET until it settles). `syncMutation.isPending` stays true for
 * that whole window, so "Sync now" naturally reads as "Syncing…" without this component needing
 * to know whether it's mid-POST or mid-poll. Closing the drawer unmounts this component, which
 * cancels the polling loop via the effect below — no dangling GETs, no toast/invalidation firing
 * for a sync the admin already navigated away from.
 */
export const BookingEngineSyncStatus: React.FC<BookingEngineSyncStatusProps> = ({ serviceId, className = '' }) => {
  const enabled = isBookingEngineSyncEnabled();
  const statusQuery = useEngineSyncStatus(serviceId);
  const syncMutation = useSyncServiceToEngine();
  const cancelSync = syncMutation.cancel;

  useEffect(() => () => cancelSync(), [cancelSync]);

  if (!enabled || !serviceId) return null;

  const isSyncing = syncMutation.isPending;
  const outcome = syncMutation.data;
  const failed = outcome?.kind === 'failed';
  const neutral = outcome?.kind === 'neutral' || outcome?.kind === 'timeout';
  const synced = outcome ? outcome.kind === 'succeeded' : !!statusQuery.data?.synced;
  const hasError = failed || statusQuery.isError;

  let badgeVariant: 'active' | 'invited' | 'inactive' = 'invited';
  let badgeLabel = 'Not synced';
  let Icon = CircleDashed;

  if (isSyncing) {
    badgeVariant = 'invited';
    badgeLabel = 'Syncing…';
    Icon = RefreshCw;
  } else if (hasError) {
    badgeVariant = 'inactive';
    badgeLabel = 'Sync error';
    Icon = AlertTriangle;
  } else if (neutral) {
    badgeVariant = 'invited';
    badgeLabel = 'Taking longer';
    Icon = Clock;
  } else if (synced) {
    badgeVariant = 'active';
    badgeLabel = 'Synced';
    Icon = CheckCircle2;
  }

  // SAM-546 — surface the backend's own EngineSyncError message (the specific field/resource/
  // call that failed, e.g. "Duplicate resource key 'cabana_1'...") instead of leaving the admin
  // with just a "Sync error" badge and no idea why. Prefers the freshest failure: a manual
  // "Sync now" click that just failed, then the last GET status check.
  // SAM-568 — a 'neutral'/'timeout' outcome is deliberately NOT an error (the backend may still
  // finish); it gets the same message line, just without the red styling below.
  const detailMessage = failed || neutral
    ? outcome!.message
    : statusQuery.isError
      ? extractEngineSyncErrorMessage(statusQuery.error)
      : statusQuery.data?.engine_status && statusQuery.data.engine_status.reachable === false
        ? (statusQuery.data.engine_status.error || 'Booking engine unreachable')
        : null;

  return (
    <div className={`rounded-lg border border-[var(--border,#E5E7EB)] bg-[var(--surface-subtle,#F9FAFB)] px-3 py-2 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon size={14} className={`shrink-0 ${isSyncing ? 'animate-spin' : ''}`} />
          <span className="text-xs font-medium text-[var(--text-secondary,#4B5563)]">Booking engine</span>
          <Badge variant={badgeVariant}>{badgeLabel}</Badge>
        </div>
        <Button
          variant="ghost"
          icon={<RefreshCw size={13} className={isSyncing ? 'animate-spin' : ''} />}
          isLoading={isSyncing}
          disabled={isSyncing || statusQuery.isLoading}
          onClick={() => syncMutation.mutate(serviceId)}
          title="Sync this service into the booking engine now"
        >
          Sync now
        </Button>
      </div>
      {isSyncing && (
        <p className="text-xs mt-1.5 text-[var(--text-secondary,#4B5563)]">
          Syncing with the booking engine…
        </p>
      )}
      {!isSyncing && (hasError || neutral) && detailMessage && (
        <p className="text-xs mt-1.5 break-words" style={{ color: hasError ? '#B91C1C' : '#92400E' }}>
          {detailMessage}
        </p>
      )}
    </div>
  );
};
